import React, { useMemo, useState, useEffect, useCallback } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { Edit, Save, Cancel, Delete, Undo } from '@mui/icons-material';
import { questionDefinitionToTranslationKeys } from '@awarego-ui/components';
import services from '../../../services';
import { useStores } from '../../../hooks/use-stores';
import { extractErrorMessage } from '../../../utils/helpers';
import { refreshI18nPreview } from '../../../utils/refreshI18nPreview';

function getTranslationFileUrl(language, namespace = 'assessment') {
    const resourcesBase = (
        import.meta.env.REACT_APP_RESOURCES_URL || 'https://resources.awarego.com'
    ).replace(/\/$/, '');
    return `${resourcesBase}/localise/locale/${language}/${namespace}.json`;
}

function overrideSettingKey(language, translationKey) {
    return `ui.translation.${language}.${translationKey}`;
}

function translationKeyFromSettingName(settingName) {
    const prefix = 'ui.translation.';
    if (!settingName.startsWith(prefix)) return settingName;
    const rest = settingName.slice(prefix.length);
    const dot = rest.indexOf('.');
    return dot >= 0 ? rest.slice(dot + 1) : rest;
}

function QuestionTranslationOverridesEditor({
    active = true,
    language = 'en',
    companyId,
    partnerId,
    customizeSaveTo = 'company',
    definition,
    questionId,
    questionName,
}) {
    const { brandingStore } = useStores();
    const [baseLocale, setBaseLocale] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [editingKey, setEditingKey] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [changes, setChanges] = useState({});
    const [savedOverrides, setSavedOverrides] = useState({});
    const [commitDialogOpen, setCommitDialogOpen] = useState(false);

    const questionKeys = useMemo(
        () => questionDefinitionToTranslationKeys(definition),
        [definition]
    );

    const saveToPartner =
        customizeSaveTo === 'partner' && Boolean(partnerId);

    const hasChanges = Object.keys(changes).length > 0;

    useEffect(() => {
        if (!active) return;
        setError(null);
        setSuccess(null);
        setChanges({});
        setEditingKey(null);
        setEditValue('');
    }, [active, definition]);

    const loadSavedOverrides = useCallback(async () => {
        if (saveToPartner && partnerId) {
            return services.Partners.getTranslationOverrides(partnerId);
        }
        if (companyId) {
            return services.Companies.getTranslationOverrides(companyId);
        }
        return {};
    }, [saveToPartner, partnerId, companyId]);

    useEffect(() => {
        if (!active) return;
        let cancelled = false;
        const loadOverrides = async () => {
            try {
                const data = await loadSavedOverrides();
                if (!cancelled) {
                    setSavedOverrides(data || {});
                    if (saveToPartner && partnerId) {
                        brandingStore.syncQuestionTranslationOverrides(
                            data || {},
                            changes
                        );
                    }
                }
            } catch (e) {
                if (!cancelled) {
                    setSavedOverrides({});
                }
            }
        };
        loadOverrides();
        return () => {
            cancelled = true;
        };
    }, [active, loadSavedOverrides]);

    useEffect(() => {
        if (!active || questionKeys.length === 0) return;
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await fetch(
                    getTranslationFileUrl(language, 'assessment')
                );
                const locale = response.ok ? await response.json() : {};
                if (!cancelled) {
                    setBaseLocale(locale || {});
                }
            } catch (e) {
                if (!cancelled) {
                    setBaseLocale({});
                    setError('Failed to load base translations for this language.');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [active, language, questionKeys]);

    useEffect(() => {
        brandingStore.syncQuestionTranslationOverrides(
            savedOverrides,
            changes
        );
        refreshI18nPreview();
    }, [savedOverrides, changes, brandingStore]);

    const getCommittedOverride = useCallback(
        (key) => {
            const settingKey = overrideSettingKey(language, key);
            return savedOverrides[settingKey];
        },
        [language, savedOverrides]
    );

    const isCustomized = useCallback(
        (key) => {
            const settingKey = overrideSettingKey(language, key);
            if (Object.prototype.hasOwnProperty.call(changes, settingKey)) {
                return changes[settingKey] !== null;
            }
            return getCommittedOverride(key) !== undefined;
        },
        [language, changes, getCommittedOverride]
    );

    const sortedQuestionKeys = useMemo(() => {
        const customized = [];
        const standard = [];
        questionKeys.forEach((key) => {
            if (isCustomized(key)) {
                customized.push(key);
            } else {
                standard.push(key);
            }
        });
        customized.sort((a, b) => a.localeCompare(b));
        standard.sort((a, b) => a.localeCompare(b));
        return [...customized, ...standard];
    }, [questionKeys, isCustomized]);

    const effectiveData = useMemo(() => {
        const result = {};
        questionKeys.forEach((key) => {
            const settingKey = overrideSettingKey(language, key);
            const pendingValue = changes[settingKey];
            if (pendingValue === null) {
                result[key] = baseLocale[key] ?? '';
                return;
            }
            if (pendingValue !== undefined) {
                result[key] = pendingValue;
                return;
            }
            const savedOverride = savedOverrides[settingKey];
            if (savedOverride !== undefined) {
                result[key] = savedOverride;
                return;
            }
            const brandingOverride = brandingStore.branding?.[settingKey];
            if (brandingOverride !== undefined) {
                result[key] = brandingOverride;
                return;
            }
            result[key] = baseLocale[key] ?? '';
        });
        return result;
    }, [
        questionKeys,
        language,
        changes,
        savedOverrides,
        brandingStore.branding,
        baseLocale,
    ]);

    const pendingChangeEntries = useMemo(() => {
        return Object.entries(changes).map(([settingKey, value]) => ({
            settingKey,
            translationKey: translationKeyFromSettingName(settingKey),
            value,
            isRollback: value === null,
        }));
    }, [changes]);

    const handleEdit = (key) => {
        setEditingKey(key);
        setEditValue(effectiveData[key] || '');
    };

    const handleSaveInline = (key) => {
        const settingKey = overrideSettingKey(language, key);
        setChanges((prev) => {
            const next = { ...prev, [settingKey]: editValue };
            brandingStore.syncQuestionTranslationOverrides(savedOverrides, next);
            refreshI18nPreview();
            return next;
        });
        setEditingKey(null);
        setEditValue('');
    };

    const applyPendingToPreview = (nextChanges) => {
        brandingStore.syncQuestionTranslationOverrides(savedOverrides, nextChanges);
        refreshI18nPreview();
    };

    const handleRevert = (key) => {
        const settingKey = overrideSettingKey(language, key);
        const hasCommitted = getCommittedOverride(key) !== undefined;
        if (hasCommitted) {
            setChanges((prev) => {
                const next = { ...prev, [settingKey]: null };
                applyPendingToPreview(next);
                return next;
            });
        } else {
            setChanges((prev) => {
                const next = { ...prev };
                delete next[settingKey];
                applyPendingToPreview(next);
                return next;
            });
        }
        if (editingKey === key) {
            setEditingKey(null);
            setEditValue('');
        }
    };

    const handleDeletePending = (settingKey) => {
        setChanges((prev) => {
            const next = { ...prev };
            delete next[settingKey];
            applyPendingToPreview(next);
            return next;
        });
    };

    const handleSaveAll = () => {
        if (!hasChanges) return;
        setCommitDialogOpen(true);
    };

    const handleCommitCancel = () => {
        setCommitDialogOpen(false);
    };

    const handleCommitSave = async () => {
        setCommitDialogOpen(false);
        if (Object.keys(changes).length === 0) return;
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
            const auditPayload = {
                overrides: changes,
                ...(questionId != null ? { questionId } : {}),
                ...(questionName ? { questionName } : {}),
            };
            if (saveToPartner) {
                await services.Partners.upsertTranslationOverrides(
                    partnerId,
                    auditPayload
                );
            } else {
                await services.Companies.upsertTranslationOverrides(
                    companyId,
                    auditPayload
                );
                await brandingStore.loadBranding(companyId);
            }
            const refreshed = await loadSavedOverrides();
            setSavedOverrides(refreshed || {});
            brandingStore.syncQuestionTranslationOverrides(refreshed || {}, {});
            await refreshI18nPreview();
            setSuccess(
                `Saved ${Object.keys(changes).length} change(s).`
            );
            setChanges({});
        } catch (e) {
            setError(
                extractErrorMessage(e) || 'Failed to save translation overrides.'
            );
        } finally {
            setSaving(false);
        }
    };

    if (!active) {
        return null;
    }

    const pendingCount = pendingChangeEntries.length;

    const renderSaveAllButton = (sx = {}) => (
        <Button
            variant="contained"
            fullWidth
            startIcon={saving ? <CircularProgress size={16} /> : <Save />}
            onClick={handleSaveAll}
            disabled={saving || !hasChanges}
            sx={sx}
        >
            Save All Changes{hasChanges ? ` (${pendingCount})` : ''}
        </Button>
    );

    const renderTable = () => (
        <Table size="small" stickyHeader>
            <TableHead>
                <TableRow>
                    <TableCell sx={{ width: '32%', fontWeight: 700 }}>Key</TableCell>
                    <TableCell sx={{ width: '53%', fontWeight: 700 }}>Value</TableCell>
                    <TableCell sx={{ width: '15%', fontWeight: 700 }}>Actions</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {sortedQuestionKeys.map((key) => {
                    const customized = isCustomized(key);
                    const settingKey = overrideSettingKey(language, key);
                    const pendingRollback = changes[settingKey] === null;
                    const baseValue = baseLocale[key] ?? '';

                    return (
                        <TableRow
                            key={key}
                            sx={
                                customized
                                    ? { bgcolor: 'action.hover' }
                                    : undefined
                            }
                        >
                            <TableCell
                                sx={{
                                    verticalAlign: 'top',
                                    wordBreak: 'break-word',
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 0.5,
                                    }}
                                >
                                    {key}
                                    {customized && (
                                        <Chip
                                            label="Customized"
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                            sx={{ alignSelf: 'flex-start' }}
                                        />
                                    )}
                                    {pendingRollback && (
                                        <Chip
                                            label="Revert pending"
                                            size="small"
                                            color="warning"
                                            variant="outlined"
                                            sx={{ alignSelf: 'flex-start' }}
                                        />
                                    )}
                                </Box>
                            </TableCell>
                            <TableCell>
                                {editingKey === key ? (
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        value={editValue}
                                        onChange={(e) =>
                                            setEditValue(e.target.value)
                                        }
                                    />
                                ) : (
                                    <Box>
                                        <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                                            {effectiveData[key]}
                                        </Typography>
                                        {customized &&
                                            effectiveData[key] !== baseValue && (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{
                                                        display: 'block',
                                                        mt: 0.5,
                                                    }}
                                                >
                                                    Default: {baseValue || '—'}
                                                </Typography>
                                            )}
                                    </Box>
                                )}
                            </TableCell>
                            <TableCell>
                                {editingKey === key ? (
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() => handleSaveInline(key)}
                                        >
                                            <Save fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setEditingKey(null);
                                                setEditValue('');
                                            }}
                                        >
                                            <Cancel fontSize="small" />
                                        </IconButton>
                                    </Box>
                                ) : (
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleEdit(key)}
                                            title="Edit"
                                        >
                                            <Edit fontSize="small" />
                                        </IconButton>
                                        {customized && (
                                            <IconButton
                                                size="small"
                                                color="warning"
                                                onClick={() => handleRevert(key)}
                                                title="Revert to default"
                                            >
                                                <Undo fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Box>
                                )}
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
            }}
        >
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert
                    severity="success"
                    sx={{ mb: 2 }}
                    onClose={() => setSuccess(null)}
                >
                    {success}
                </Alert>
            )}

            {questionKeys.length === 0 ? (
                <Alert severity="info">
                    No translatable keys were found for this question.
                </Alert>
            ) : (
                <Box
                    sx={{
                        display: 'flex',
                        flex: 1,
                        minHeight: 0,
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        {loading ? (
                            <Box
                                sx={{
                                    py: 5,
                                    display: 'flex',
                                    justifyContent: 'center',
                                }}
                            >
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Paper
                                variant="outlined"
                                sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}
                            >
                                <TableContainer>{renderTable()}</TableContainer>
                            </Paper>
                        )}
                    </Box>

                    <Box
                        sx={{
                            width: 320,
                            flexShrink: 0,
                            borderLeft: 1,
                            borderColor: 'divider',
                            ml: 2,
                            pl: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            minHeight: 0,
                            overflow: 'auto',
                        }}
                    >
                        <Typography variant="h6" gutterBottom>
                            Pending changes
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        {!hasChanges ? (
                            <Typography
                                color="text.secondary"
                                sx={{ textAlign: 'center', mt: 4 }}
                            >
                                No pending changes
                            </Typography>
                        ) : (
                            <>
                                {renderSaveAllButton({ mb: 2, flexShrink: 0 })}
                                <List dense sx={{ flex: 1, overflow: 'auto' }}>
                                    {pendingChangeEntries.map(
                                        ({ settingKey, translationKey, value, isRollback }) => (
                                            <ListItem
                                                key={settingKey}
                                                sx={{
                                                    display: 'block',
                                                    bgcolor: 'grey.50',
                                                    mb: 1,
                                                    borderRadius: 1,
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                }}
                                                secondaryAction={
                                                    <IconButton
                                                        edge="end"
                                                        size="small"
                                                        aria-label="remove pending change"
                                                        onClick={() =>
                                                            handleDeletePending(settingKey)
                                                        }
                                                    >
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                }
                                            >
                                                <ListItemText
                                                    primary={
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontWeight: 700,
                                                                wordBreak: 'break-word',
                                                                pr: 3,
                                                            }}
                                                        >
                                                            {translationKey}
                                                        </Typography>
                                                    }
                                                    secondary={
                                                        <>
                                                            <Typography
                                                                variant="caption"
                                                                color={
                                                                    isRollback
                                                                        ? 'warning.main'
                                                                        : 'primary'
                                                                }
                                                                component="span"
                                                                sx={{ display: 'block' }}
                                                            >
                                                                {isRollback
                                                                    ? 'Revert (remove override)'
                                                                    : 'Edit'}
                                                            </Typography>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    whiteSpace: 'pre-wrap',
                                                                    wordBreak: 'break-word',
                                                                    fontStyle:
                                                                        isRollback
                                                                            ? 'italic'
                                                                            : 'normal',
                                                                    color: isRollback
                                                                        ? 'text.secondary'
                                                                        : 'inherit',
                                                                }}
                                                            >
                                                                {isRollback
                                                                    ? '<revert to default>'
                                                                    : value === ''
                                                                      ? '<EMPTY>'
                                                                      : value}
                                                            </Typography>
                                                        </>
                                                    }
                                                />
                                            </ListItem>
                                        )
                                    )}
                                </List>

                                {renderSaveAllButton({ mt: 2, flexShrink: 0 })}
                            </>
                        )}
                    </Box>
                </Box>
            )}

            <Dialog
                open={commitDialogOpen}
                onClose={handleCommitCancel}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Confirm save</DialogTitle>
                <DialogContent>
                    <Typography variant="body1">
                        Save {pendingChangeEntries.length} pending change(s)?
                    </Typography>
                    {pendingChangeEntries.some((e) => e.isRollback) && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Includes{' '}
                            {
                                pendingChangeEntries.filter((e) => e.isRollback)
                                    .length
                            }{' '}
                            revert(s) that restore default locale text.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCommitCancel}>Cancel</Button>
                    <Button
                        onClick={handleCommitSave}
                        variant="contained"
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={16} /> : <Save />}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default QuestionTranslationOverridesEditor;
